import {
	EmptyState,
	PageHeader,
	PaginationPageDefault,
	TableCard,
} from "@components";
import { Button } from "@components/button/button-untitledui";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import { getLocalTimeZone, today } from "@internationalized/date";
import { useCallback, useMemo, useState } from "react";
import { useDebouncedCommit } from "@/shared/hooks/use-debounced-commit";
import { EventsExportModal } from "../components/events-export-modal";
import {
	createDefaultEventsFilters,
	EventsFilters,
	type EventsFiltersState,
} from "../components/events-filters";
import { ListingDataTable } from "../components/listing-data-table";
import {
	EVENTS_TABLE_COLUMNS,
	TRACKING_EVENT_TYPE_SET,
	TRACKING_EVENTS_DEFAULT_QUERY_TYPES,
} from "../constants/events";
import { useEventsList } from "../hooks/use-events-list";
import {
	renderEventsCell,
	renderEventsDetailRow,
} from "../utils/events-render";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;

const normalizeSearch = (value: string) => value.trim();

export default function TrackingEventsPage() {
	const [page, setPage] = useState(1);
	const [isExportModalOpen, setIsExportModalOpen] = useState(false);
	const [filters, setFilters] = useState<EventsFiltersState>(
		createDefaultEventsFilters
	);
	const [searchInput, setSearchInput] = useState("");

	const handleClear = useCallback(() => {
		setSearchInput("");
		setFilters(createDefaultEventsFilters());
		setPage(1);
	}, []);

	const handleFiltersChange = useCallback((next: EventsFiltersState) => {
		const todayDate = today(getLocalTimeZone());
		if (next.endDate.toString() < next.startDate.toString()) {
			setFilters({ ...next, endDate: next.startDate });
			return;
		}
		if (next.endDate.toString() > todayDate.toString()) {
			setFilters({ ...next, endDate: todayDate });
			return;
		}
		setFilters(next);
	}, []);

	const applySearch = useCallback((term: string) => {
		setFilters((prev) => ({ ...prev, search: term }));
		setPage(1);
	}, []);

	useDebouncedCommit({
		value: searchInput,
		applied: filters.search,
		delay: SEARCH_DEBOUNCE_MS,
		normalize: normalizeSearch,
		onCommit: applySearch,
	});

	const criteria = useMemo(
		() => ({
			startDate: filters.startDate.toString(),
			endDate: filters.endDate.toString(),
			pageSize: PAGE_SIZE,
			...(filters.search.trim() && { search: filters.search.trim() }),
			types:
				filters.types?.length && filters.types.length > 0
					? filters.types
					: TRACKING_EVENTS_DEFAULT_QUERY_TYPES,
		}),
		[filters.startDate, filters.endDate, filters.search, filters.types]
	);

	const queryParams = useMemo(
		() => ({ ...criteria, pageNumber: page }),
		[criteria, page]
	);

	const eventsQuery = useEventsList(queryParams);
	const { data, isLoading, isError, refetch } = eventsQuery;

	const { total: totalPages } = usePaginationTotal({
		page,
		onPageChange: setPage,
		criteria,
		totalPages: data?.totalPages,
		query: eventsQuery,
	});

	const items = (data?.items ?? []).filter((item) =>
		TRACKING_EVENT_TYPE_SET.has(item.eventType)
	);

	const showPagination = totalPages !== null && totalPages > 1;
	const hasListData = !isError && items.length > 0;

	const emptyContent = (
		<EmptyState className="w-full" size="md">
			<EmptyState.Header pattern="none">
				<EmptyState.FeaturedIcon className="z-0" color="gray" theme="modern" />
			</EmptyState.Header>
			<EmptyState.Content className="z-auto">
				<EmptyState.Title>Nenhum evento encontrado</EmptyState.Title>
				<EmptyState.Description>
					Não localizamos ocorrências para os filtros selecionados. Verifique se
					o nome do motorista ou a placa estão corretos.
				</EmptyState.Description>
			</EmptyState.Content>
			<EmptyState.Footer className="z-auto justify-center">
				<Button color="secondary" onClick={handleClear} size="sm">
					Limpar filtros
				</Button>
			</EmptyState.Footer>
		</EmptyState>
	);

	const hasTotal = !(isLoading || isError) && data !== undefined;

	return (
		<div className="flex flex-col gap-6 px-6 py-8">
			<PageHeader title="Eventos" />

			<EventsFilters
				filters={filters}
				hasListData={hasListData}
				onExport={() => setIsExportModalOpen(true)}
				onFiltersChange={handleFiltersChange}
				onPageChange={setPage}
				onSearchInputChange={setSearchInput}
				searchInput={searchInput}
			/>

			<TableCard.Root variant="listing">
				<ListingDataTable
					ariaLabel="Lista de eventos de telemetria"
					cardHeader={{
						title: "Eventos Registrados",
						description: "Listagem completa de ocorrências registradas",
						trailing: hasTotal ? (
							<div className="flex flex-col items-start">
								<span className="text-sm text-tertiary">Total de Eventos</span>
								<span className="font-semibold text-2xl text-primary">
									{data?.totalCount ?? 0}
								</span>
							</div>
						) : null,
					}}
					columns={EVENTS_TABLE_COLUMNS}
					emptyContent={emptyContent}
					errorMessage="Não foi possível carregar os eventos."
					expandable
					getRowKey={(item) => item.id}
					isError={isError}
					isLoading={isLoading}
					items={items}
					loadingLabel="Carregando eventos"
					onRetry={() => {
						refetch();
					}}
					renderCell={(item, col, colIndex) =>
						renderEventsCell(item, col, colIndex)
					}
					renderExpandedRow={renderEventsDetailRow}
					variant="listing"
				/>
				{showPagination ? (
					<PaginationPageDefault
						className="p-5"
						onPageChange={setPage}
						page={page}
						total={totalPages}
					/>
				) : null}
			</TableCard.Root>

			<EventsExportModal
				isOpen={isExportModalOpen}
				onClose={() => setIsExportModalOpen(false)}
			/>
		</div>
	);
}
